import { useEffect, useRef, useState } from "react";
import { Copy, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText, fmtDate } from "./api";

const MAX_SIZE = 5 * 1024 * 1024;
const TYPES = ["image/jpeg", "image/png", "image/webp", "image/svg+xml"];

export default function Media() {
  const [assets, setAssets] = useState(null);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => adminApi.get("/admin/media").then((r) => setAssets(r.data)).catch(() => toast.error("Could not load media."));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!TYPES.includes(file.type)) { toast.error("Only JPG, PNG, WebP or SVG images are allowed."); return; }
    if (file.size > MAX_SIZE) { toast.error("File too large (max 5 MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await adminApi.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Image uploaded.");
      load();
    } catch (err) {
      toast.error(errText(err, "Upload failed. Please retry."));
    } finally {
      setUploading(false);
    }
  };

  const copyUrl = async (m) => {
    await navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}${m.url}`);
    toast.success("Image URL copied. Paste it anywhere an image is needed.");
  };

  const remove = async (m) => {
    if (!window.confirm(`Are you sure you want to delete "${m.original_filename}"? This action cannot be undone.`)) return;
    try {
      await adminApi.delete(`/admin/media/${m.id}`);
      toast.success("Image deleted.");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const saveAlt = async (m, alt_text) => {
    try {
      await adminApi.put(`/admin/media/${m.id}`, { alt_text });
      toast.success("Alt text saved.");
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const list = (assets || []).filter((m) => !q || m.original_filename.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="media-manager">
      <div className="admin-page-head">
        <h1>Media Manager</h1>
        <p>Upload and manage website images. Copy an image URL to use it in Team & Services or share with your developer.</p>
      </div>
      <div className="admin-toolbar">
        <div className="search-box">
          <Search size={15} />
          <input placeholder="Search by file name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search media" data-testid="media-search-input" />
        </div>
        <div>
          <input type="file" accept={TYPES.join(",")} ref={fileRef} onChange={upload} hidden data-testid="media-file-input" />
          <button className="btn btn-primary" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-button">
            <Upload size={15} /> {uploading ? "Uploading…" : "Upload image"}
          </button>
        </div>
      </div>
      {!assets && <p className="admin-loading">Loading media…</p>}
      {assets && list.length === 0 && <p className="empty-note" data-testid="media-empty">No media yet. Upload your first image (JPG, PNG, WebP or SVG, max 5 MB).</p>}
      <div className="media-grid">
        {list.map((m) => (
          <figure className="media-card" key={m.id} data-testid="media-card">
            <div className="media-thumb"><img src={`${process.env.REACT_APP_BACKEND_URL}${m.url}`} alt={m.alt_text || m.original_filename} loading="lazy" /></div>
            <figcaption>
              <strong title={m.original_filename}>{m.original_filename}</strong>
              <small>{(m.size / 1024).toFixed(0)} KB · {fmtDate(m.created_at)}</small>
              <input defaultValue={m.alt_text} placeholder="Alt text" onBlur={(e) => e.target.value !== m.alt_text && saveAlt(m, e.target.value)} aria-label="Alt text" data-testid="media-alt-input" />
              <div className="admin-actions">
                <button className="icon-btn" onClick={() => copyUrl(m)} aria-label="Copy image URL" data-testid="media-copy-button"><Copy size={14} /> Copy URL</button>
                <button className="icon-btn danger" onClick={() => remove(m)} aria-label="Delete image" data-testid="media-delete-button"><Trash2 size={14} /></button>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}
